import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Heart, MessageCircle, ImagePlus, Send, Crown, Globe, MapPin, Users, Loader2, X, Megaphone, Rocket, Flag } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const SCOPES = [
  { key: "global", label: "Global", icon: Globe },
  { key: "city", label: "My City", icon: MapPin },
  { key: "team", label: "Team", icon: Users },
];

export default function Community() {
  const { user, applyUser } = useAuth();
  const navigate = useNavigate();
  const [scope, setScope] = useState("global");
  const [posts, setPosts] = useState([]);
  const [text, setText] = useState("");
  const [image, setImage] = useState(null);
  const [posting, setPosting] = useState(false);
  const [openComments, setOpenComments] = useState(null);
  const [promote, setPromote] = useState(null);
  const fileRef = useRef();

  const load = async () => {
    try { const { data } = await api.get("/feed", { params: { scope } }); setPosts(data); } catch {}
  };
  useEffect(() => { load(); }, [scope]);

  const uploadImg = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setImage(data.path); }
    catch (e) { toast.error(apiError(e)); }
  };

  const submit = async () => {
    if (!text.trim() && !image) return;
    setPosting(true);
    try {
      await api.post("/feed", { text, image_path: image, scope });
      setText(""); setImage(null); toast.success("Posted! +10 XP"); load();
    } catch (e) { toast.error(apiError(e)); } finally { setPosting(false); }
  };

  const like = async (pid) => {
    try {
      const { data } = await api.post(`/feed/${pid}/like`);
      setPosts((ps) => ps.map((p) => p.id === pid ? { ...p, liked: data.liked, like_count: data.like_count } : p));
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2"><Users className="w-6 h-6 text-jixxeo-blue" /><h2 className="font-head font-extrabold text-xl">Community</h2></div>

      <div className="flex gap-2">
        {SCOPES.map((s) => { const I = s.icon; return (
          <button key={s.key} onClick={() => setScope(s.key)} data-testid={`scope-${s.key}`}
            className={`flex-1 py-2 rounded-full text-sm font-bold flex items-center justify-center gap-1.5 ${scope === s.key ? "bg-jixxeo-blue text-white" : "glass text-muted-foreground"}`}>
            <I className="w-4 h-4" />{s.label}
          </button>
        ); })}
      </div>

      {/* Composer */}
      <div className="glass rounded-2xl p-4">
        <div className="flex gap-3">
          <Avatar user={user} size={40} />
          <div className="flex-1">
            <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={`Share a ${scope === "city" ? user?.city || "city" : scope} cleanup…`} data-testid="post-text" className="bg-white/5 border-white/10 rounded-xl resize-none min-h-[60px]" />
            {image && (
              <div className="relative mt-2 inline-block">
                <img src={fileUrl(image)} alt="" className="h-24 rounded-xl object-cover" />
                <button onClick={() => setImage(null)} className="absolute -top-2 -right-2 bg-jixxeo-danger rounded-full p-1"><X className="w-3 h-3 text-white" /></button>
              </div>
            )}
            <div className="flex items-center justify-between mt-2">
              <button onClick={() => fileRef.current.click()} className="text-muted-foreground hover:text-jixxeo-green flex items-center gap-1 text-sm" data-testid="post-image-btn"><ImagePlus className="w-5 h-5" />Photo</button>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && uploadImg(e.target.files[0])} />
              <Button onClick={submit} disabled={posting} data-testid="post-submit" size="sm" className="rounded-full bg-jixxeo-green text-black font-bold hover:bg-jixxeo-greenDark">{posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" />Post</>}</Button>
            </div>
          </div>
        </div>
      </div>

      {posts.length === 0 ? (
        <div className="glass rounded-2xl p-8 text-center text-muted-foreground"><Users className="w-10 h-10 mx-auto mb-2 opacity-40" />No posts yet. Start the conversation!</div>
      ) : (
        <div className="space-y-3">
          {posts.map((p) => (
            <div key={p.id} className={`glass rounded-2xl p-4 ${p.promoted ? "border border-jixxeo-gold/50 neon-gold" : ""}`} data-testid={`post-${p.id}`}>
              {p.promoted && (
                <div className="flex items-center gap-1.5 text-jixxeo-gold text-[11px] font-bold mb-2" data-testid={`promoted-badge-${p.id}`}>
                  <Megaphone className="w-3.5 h-3.5" />FEATURED
                </div>
              )}
              <div className="flex items-center gap-3">
                <button onClick={() => navigate(`/profile/${p.user_id}`)}><Avatar user={{ name: p.author_name, avatar: p.author_avatar, elite: p.author_elite }} size={40} /></button>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm flex items-center gap-1">{p.author_name}{p.author_elite && <Crown className="w-3.5 h-3.5 text-jixxeo-purple" />}</p>
                  <p className="text-[10px] text-muted-foreground">{new Date(p.created_at).toLocaleString()}</p>
                </div>
              </div>
              {p.text && <p className="text-sm mt-3 whitespace-pre-wrap">{p.text}</p>}
              {p.image_path && <img src={fileUrl(p.image_path)} alt="" className="w-full rounded-xl mt-3 object-cover max-h-80" />}
              <div className="flex items-center gap-5 mt-3 text-sm">
                <button onClick={() => like(p.id)} data-testid={`like-${p.id}`} className={`flex items-center gap-1.5 ${p.liked ? "text-jixxeo-danger" : "text-muted-foreground"}`}>
                  <Heart className={`w-5 h-5 ${p.liked ? "fill-jixxeo-danger" : ""}`} />{p.like_count}
                </button>
                <button onClick={() => setOpenComments(openComments === p.id ? null : p.id)} data-testid={`comment-${p.id}`} className="flex items-center gap-1.5 text-muted-foreground"><MessageCircle className="w-5 h-5" />{p.comment_count}</button>
                {p.user_id === user?.id && !p.promoted && (
                  <button onClick={() => setPromote(p)} data-testid={`promote-${p.id}`} className="flex items-center gap-1.5 text-jixxeo-gold ml-auto font-bold"><Rocket className="w-4 h-4" />Promote</button>
                )}
              </div>
              {openComments === p.id && <Comments post={p} onAdded={load} />}
            </div>
          ))}
        </div>
      )}
      {promote && <PromoteDialog post={promote} onClose={() => setPromote(null)} applyUser={applyUser} onDone={load} />}
    </div>
  );
}

function PromoteDialog({ post, onClose, applyUser, onDone }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/feed/${post.id}/promote`);
      applyUser(data.user);
      toast.success(`Post featured for ${data.hours}h! (−${data.cost} coins)`);
      onDone?.(); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="glass border-white/10 max-w-sm text-center" data-testid="promote-dialog">
        <DialogHeader><DialogTitle className="flex items-center justify-center gap-2"><Megaphone className="w-5 h-5 text-jixxeo-gold" />Feature this post</DialogTitle></DialogHeader>
        <div className="w-14 h-14 mx-auto rounded-full bg-jixxeo-gold/15 flex items-center justify-center"><Rocket className="w-8 h-8 text-jixxeo-gold" /></div>
        <p className="text-sm text-muted-foreground">Pin your cleanup photo to the top of the <span className="text-white font-bold">global feed</span> so every hunter sees it for 24 hours.</p>
        <p className="font-display text-2xl text-jixxeo-gold">150 🪙</p>
        <Button onClick={go} disabled={busy} data-testid="promote-confirm" className="w-full rounded-full bg-jixxeo-gold text-black font-bold hover:bg-yellow-400">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Promote Now"}</Button>
      </DialogContent>
    </Dialog>
  );
}

function Comments({ post, onAdded }) {
  const [text, setText] = useState("");
  const [comments, setComments] = useState(post.comments || []);
  const add = async () => {
    if (!text.trim()) return;
    try { const { data } = await api.post(`/feed/${post.id}/comment`, { text }); setComments((c) => [...c, data]); setText(""); onAdded?.(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="mt-3 pt-3 border-t border-white/10 space-y-2">
      {comments.map((c) => (
        <div key={c.id} className="flex gap-2 text-sm"><span className="font-bold text-jixxeo-blue">{c.author_name}:</span><span className="text-white/80">{c.text}</span></div>
      ))}
      <div className="flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Add a comment…" data-testid={`comment-input-${post.id}`} className="flex-1 bg-white/5 border border-white/10 rounded-full px-3 h-9 text-sm" />
        <Button onClick={add} size="sm" data-testid={`comment-send-${post.id}`} className="rounded-full bg-jixxeo-blue text-white h-9"><Send className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}
